import { motion } from "framer-motion";
import QuoteModal from "@/components/QuoteModal";
const aboutImg = "/images/site/refinished-red-oak-floor-sunlit-corner-livonia-mi.webp";
import { Check } from "lucide-react";
import { NWFA_CREDENTIALS } from "@/data/credentials";

// One-line context for each NWFA certification, keyed by the exact name in
// credentials.js. Adding a certification requires adding a matching entry.
const NWFA_CONTEXT: Record<string, string> = {
  "NWFA Certified Installer":
    "Expertise across every species, substrate, and installation method.",
  "NWFA Certified Sand & Finisher":
    "Dustless sand and finish systems including waterborne, oil, hardwax, and instant UV cure.",
  "NWFA Certified Wood Flooring Inspector":
    "We don't guess. We diagnose with forensic precision and scientific data.",
  "NWFA Certified Sales Advisor":
    "Specification, species selection, and system design for architects, builders, and homeowners.",
};

const credentials = [
  ...NWFA_CREDENTIALS.map((cert) => `${cert}. ${NWFA_CONTEXT[cert] ?? ""}`.trim()),
  "Licensed Michigan Residential Builder. The structural insight that separates us from every other flooring company.",
  "10 Years in Manufacturing. We understand what makes a floor exceptional long before it reaches your home.",
  "Fully Insured. Your investment is protected on every project, without exception.",
];

const AboutSection = () => (
  <section className="bg-background py-14 md:py-20 lg:py-28" id="about">
    <div className="max-w-[1200px] mx-auto px-5 md:px-12 grid md:grid-cols-2 gap-8 md:gap-12 items-stretch">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="relative"
      >
        <div className="rounded-[20px] overflow-hidden h-full min-h-[280px]">
          <img width={1600} height={1067} loading="lazy" src={aboutImg} alt="Sunlit corner with a refinished red oak floor, Livonia, MI" className="w-full h-full object-cover" />
        </div>
        <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 bg-foreground/90 backdrop-blur-sm rounded-[14px] px-4 md:px-6 py-3 md:py-5 text-primary-foreground">
          <span className="font-display text-3xl md:text-4xl font-semibold leading-none block">Family Owned</span>
          <span className="text-[9px] md:text-[10px] font-semibold tracking-[.14em] uppercase text-primary-foreground/60 mt-1 block">in Livonia</span>
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
      >
        <p className="text-[11px] font-semibold tracking-[.1em] uppercase text-gold-400 mb-2">About 1 Wood Floors</p>
        <h2 className="font-display text-[clamp(26px,4.5vw,52px)] font-semibold text-foreground leading-[1.1] mb-4 md:mb-6">
          Michigan's most <em className="italic font-normal text-primary">credentialed</em> flooring team.
        </h2>
        <p className="text-[13px] md:text-[15px] font-light text-neutral-600 leading-relaxed mb-3 md:mb-4">
          Ed Malaski founded 1 Wood Floors on a simple conviction: every client deserves the same attention to detail, whatever the size of the home. That belief built Michigan's most decorated wood flooring operation. Manufacturer, retailer, and NWFA authority, all under one roof.
        </p>
        <p className="text-[13px] md:text-[15px] font-light text-neutral-600 leading-relaxed mb-6 md:mb-8">
          We're not a big box retailer. We're a family owned Livonia business with manufacturing roots, Four NWFA Certifications on one team (Installer, Sand &amp; Finisher, Wood Flooring Inspector, and Sales Advisor), and a licensed builder's structural understanding. That combination simply doesn't exist anywhere else in the state.
        </p>
        <div className="flex flex-col gap-3 md:gap-4 mb-6 md:mb-8">
          {credentials.map((cred) => (
            <div key={cred} className="flex items-start gap-3">
              <div className="mt-0.5 w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                <Check className="w-3 h-3 text-primary" />
              </div>
              <span className="text-[12px] md:text-sm text-foreground leading-snug">{cred}</span>
            </div>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <a href="tel:2483258853" className="bg-foreground text-primary-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-primary transition-colors inline-flex items-center justify-center gap-2">
            📞 Call (248) 325-8853
          </a>
           <QuoteModal
             trigger={
               <button className="border border-border text-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-neutral-50 transition-colors text-center">
                 Schedule a Call
               </button>
             }
           />
        </div>
      </motion.div>
    </div>
  </section>
);

export default AboutSection;
